import React, { useState } from 'react';
import { Spin, Empty, Tooltip } from 'antd';
import { LoadingOutlined, CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { ecrireExcel, nomFichier } from './excelUtils';
import { C } from './TableauReporting';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { localeIntl } from '../../../i18n/format';

const tx = (cle, options) => i18n.t(`statistiques:reporting.${cle}`, options);

// ── Formatage ─────────────────────────────────────────────────────
const fmtN = (v) => v ? new Intl.NumberFormat(localeIntl()).format(v) : '-';
const fmtM = (v) => v ? new Intl.NumberFormat(localeIntl(), { minimumFractionDigits: 0 }).format(Number(v)) : '-';
const fmtPct = (v) => v ? (v * 100).toFixed(1) + '%' : '-';

// ── Label d'une période ───────────────────────────────────────────
const labelPeriode = (annee, anneeMin) =>
  annee === -1 ? i18n.t('statistiques:commun.etAnterieures', { annee: anneeMin - 1 }) : String(annee);

// ── Couleurs des diagonales ───────────────────────────────────────
const BG_DIAG = [
  '#dcfce7', '#d1fae5', '#bbf7d0', '#86efac', '#4ade80',
];
const bgCell = (surv, pay, periodes) => {
  if (surv === -1 || pay === -1) return '#f1f5f9';
  const iSurv = periodes.indexOf(surv);
  const iPay  = periodes.indexOf(pay);
  if (iSurv === -1 || iPay === -1) return '#fff';
  const diag = iPay - iSurv; // 0 = même année, >0 = règlement tardif
  if (diag < 0) return '#fef9c3'; // paiement avant survenance (reprise)
  return BG_DIAG[Math.min(diag, BG_DIAG.length - 1)];
};

// ── Styles ────────────────────────────────────────────────────────
const TH = (bg = '#1e293b', w) => ({
  background: bg, color: '#fff', padding: '6px 8px', textAlign: 'center',
  fontSize: 11, fontWeight: 700, border: '1px solid #334155', whiteSpace: 'nowrap',
  ...(w ? { minWidth: w } : {}),
});
const TD = (bg = '#fff', bold = false) => ({
  padding: '4px 7px', textAlign: 'right', fontSize: 11,
  border: `1px solid ${C.border}`, background: bg,
  fontWeight: bold ? 700 : 400, color: bold ? C.ink : '#334155',
  fontFamily: 'var(--font-main)',
});
const TDL = (bg = '#fff', bold = false) => ({
  ...TD(bg, bold), textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
  paddingLeft: 10, minWidth: 110,
});

// ══════════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════
export function exportCadence(data) {
  if (!data) return;
  const anneeMin = data.anneeRef - 3;
  const header = [tx('excel.survenusEn'), ...data.anneesColonnes.map(a => labelPeriode(a, anneeMin)),
                  tx('excel.totalNb'), tx('excel.totalMt'), tx('cadence.dec'), tx('cadence.taux')];

  const blocToAoa = (bloc) => {
    const rows = [['', bloc.label]];
    rows.push(header);
    for (const ligne of bloc.lignes) {
      const nbRow  = [labelPeriode(ligne.anneeSurvenance, anneeMin), tx('excel.nbre')];
      const mtRow  = ['', tx('excel.montant')];
      for (const a of data.anneesColonnes) {
        const cell = ligne.cellules[a] || { nb: 0, montant: 0 };
        nbRow.push(cell.nb);
        mtRow.push(Number(cell.montant));
      }
      nbRow.push(ligne.nbTotal, '', ligne.sinistresDeClares,
                 ligne.sinistresDeClares ? ligne.nbTotal / ligne.sinistresDeClares : '');
      mtRow.push('', ligne.montantTotal, '', '');
      rows.push(nbRow, mtRow);
    }
    rows.push([]);
    return rows;
  };

  const allAoa = [
    ['BNCB-TOGO', tx('excel.titreCadence'), data.anneeRef],
    [],
    ...blocToAoa(data.total),
    [],
    [tx('excel.parPays')],
    ...data.parPays.flatMap(b => blocToAoa(b)),
    [tx('excel.parCompagnie')],
    ...data.parCompagnie.flatMap(b => blocToAoa(b)),
  ];

  ecrireExcel([{
    name: tx('excel.feuilles.cadence'),
    aoa: allAoa,
    colWidths: [16, 8, ...Array(data.anneesColonnes.length * 2).fill(10), 8, 14, 8, 8],
  }], `Cadence_${data.anneeRef}`);
  toast.success(tx('excel.succes.cadence'));
}

export default function TabCadence({ data, loading }) {
  const { t } = useTranslation('statistiques');
  const [expanded, setExpanded] = useState({ total: true });

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: C.green }} />} />
    </div>
  );
  if (!data) return <Empty description={t('commun.aucuneDonnee')} style={{ padding: 40 }} />;

  const anneeMin = data.anneeRef - 3;
  const toggle   = (key) => setExpanded(s => ({ ...s, [key]: !s[key] }));

  const blocs = [
    { key: 'total',     label: 'TOTAL',                              bloc: data.total,    isPays: false },
    ...data.parPays.map((b, i) => ({
      key: `pays-${i}`, label: b.label, bloc: b, isPays: true
    })),
    ...data.parCompagnie.map((b, i) => ({
      key: `comp-${i}`, label: b.label, bloc: b, isPays: false
    })),
  ];

  return (
    <div>
      <style>{`
        .cad-table { border-collapse: collapse; width: 100%; }
        .cad-table tr:hover td { filter: brightness(0.96); }
        .cad-section { margin-bottom: 12px; border-radius: 10px; overflow: hidden;
          border: 1px solid ${C.border}; }
        .cad-toggle { cursor: pointer; user-select: none; }
        .cad-toggle:hover { opacity: 0.85; }
      `}</style>

      {/* Légende diagonale */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center',
        marginBottom: 16, fontSize: 11, color: C.muted }}>
        <span style={{ fontWeight: 600, color: C.ink }}>{t('reporting.cadence.lecture')}</span>
        {['memeAnnee', 'plus1', 'plus2', 'plus3', 'plus4'].map((cle) => t(`reporting.cadence.diagonales.${cle}`)).map((l, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3,
              background: BG_DIAG[Math.min(i, BG_DIAG.length-1)],
              border: `1px solid ${C.border}`, display: 'inline-block' }} />
            {l}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: '#fef9c3',
            border: `1px solid ${C.border}`, display: 'inline-block' }} />
          {t('reporting.cadence.repriseHistorique')}
        </span>
      </div>

      {/* Séparateurs par section */}
      {expanded.total && (
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600,
          letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
          {t('reporting.cadence.vueConsolidee')}
        </div>
      )}

      {blocs.map(({ key, label, bloc }) => (
        <div key={key} className="cad-section">
          {/* En-tête bloc */}
          <div className="cad-toggle"
            onClick={() => toggle(key)}
            style={{ background: '#1e293b', color: '#fff', padding: '8px 14px',
              display: 'flex', alignItems: 'center', gap: 10 }}>
            {expanded[key] ? <CaretDownOutlined /> : <CaretRightOutlined />}
            <span style={{ fontWeight: 700, fontSize: 12 }}>{label}</span>
          </div>

          {expanded[key] && (
            <div style={{ overflowX: 'auto' }}>
              <BlocTable
                bloc={bloc}
                periodes={data.anneesColonnes}
                anneeRef={data.anneeRef}
                anneeMin={anneeMin}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Triangle d'un bloc ────────────────────────────────────────────
function BlocTable({ bloc, periodes, anneeRef, anneeMin }) {
  const { t } = useTranslation('statistiques');
  return (
    <table className="cad-table">
      <thead>
        <tr>
          <th colSpan={2} style={TH('#334155', 110)}>{t('reporting.cadence.survenusEn')}</th>
          {periodes.map(a => (
            <th key={a} colSpan={2} style={TH(a === anneeRef ? '#166534' : '#1e3a5f')}>
              {t('reporting.cadence.payesEn', { annee: labelPeriode(a, anneeMin) })}
            </th>
          ))}
          <th colSpan={2} style={TH('#4a1942')}>TOTAL</th>
          <th style={TH('#334155')}>{t('reporting.cadence.dec')}</th>
          <th style={TH('#334155')}>{t('reporting.cadence.taux')}</th>
        </tr>
        <tr>
          <th colSpan={2} style={{ ...TH('#475569'), fontSize: 10 }}>{t('reporting.cadence.annee')}</th>
          {periodes.map(a => (
            <React.Fragment key={a}>
              <th style={{ ...TH(a === anneeRef ? '#1f4035' : '#162d47'), fontSize: 10 }}>
                {t('reporting.tableau.nb')}
              </th>
              <th style={{ ...TH(a === anneeRef ? '#1f4035' : '#162d47'), fontSize: 10 }}>
                {t('reporting.cadence.mtFcfa')}
              </th>
            </React.Fragment>
          ))}
          <th style={{ ...TH('#3b1238'), fontSize: 10 }}>{t('reporting.tableau.nb')}</th>
          <th style={{ ...TH('#3b1238'), fontSize: 10 }}>{t('reporting.tableau.mt')}</th>
          <th style={{ ...TH('#475569'), fontSize: 10 }}>{t('reporting.tableau.nb')}</th>
          <th style={{ ...TH('#475569'), fontSize: 10 }}>%</th>
        </tr>
      </thead>
      <tbody>
        {bloc.lignes.map((ligne, i) => {
          const isAnt = ligne.anneeSurvenance === -1;
          const rowBg = isAnt ? '#f8fafc' : (i % 2 === 0 ? '#fff' : '#fafafa');
          return (
            <tr key={i}>
              <td colSpan={2} style={TDL(rowBg, isAnt)}>
                {labelPeriode(ligne.anneeSurvenance, anneeMin)}
              </td>
              {periodes.map(pay => {
                const cell = ligne.cellules[pay] || { nb: 0, montant: 0 };
                const bg   = bgCell(ligne.anneeSurvenance, pay, periodes);
                return (
                  <React.Fragment key={pay}>
                    <td style={TD(bg)}>
                      {cell.nb > 0 ? fmtN(cell.nb) : <span style={{ color: C.muted }}>-</span>}
                    </td>
                    <td style={{ ...TD(bg), minWidth: 100 }}>
                      {Number(cell.montant) > 0
                        ? fmtM(cell.montant)
                        : <span style={{ color: C.muted }}>-</span>}
                    </td>
                  </React.Fragment>
                );
              })}
              {/* TOTAL */}
              <td style={TD('#f0fdf4', true)}>{ligne.nbTotal > 0 ? fmtN(ligne.nbTotal) : '-'}</td>
              <td style={{ ...TD('#dcfce7', true), minWidth: 110 }}>
                {Number(ligne.montantTotal) > 0 ? fmtM(ligne.montantTotal) : '-'}
              </td>
              {/* Déclarés + Taux */}
              <td style={TD('#f8fafc')}>
                {ligne.sinistresDeClares > 0 ? fmtN(ligne.sinistresDeClares) : '-'}
              </td>
              <td style={{ ...TD('#f8fafc'), fontWeight: 600,
                color: ligne.tauxPaye > 0.8 ? '#16a34a'
                     : ligne.tauxPaye > 0.4 ? '#d97706' : C.ink }}>
                {ligne.sinistresDeClares > 0 ? fmtPct(ligne.tauxPaye) : '-'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
